import _ from 'lodash';

import { canUseDOM } from 'exenv';
import { createContext, useContext, useMemo } from 'react';
import {
  generatePath,
  json,
  matchPath,
  matchRoutes,
  Navigate,
  PathMatch,
  redirect,
  RouteObject,
  useLocation,
  useNavigate,
  useParams
} from 'react-router';
import { SUPPORTED_LANGUAGE_PATH_ROOTS } from '../../../common/localeShared';
import { TServerPreloadActions } from '../../types/ssr';
import { RouteErrorBoundary } from './routeErrorBoundary';
import type { TRouteSsrMode } from './routeSsrMetadata';

export function matchRoutePath(
  pattern: Parameters<typeof matchPath>[0],
  pathname: Parameters<typeof matchPath>[1]
): PathMatch<string> | null {
  const match = matchPath(pattern, pathname);
  if (!match) {
    return null;
  }
  const decodedParams: Record<string, string | undefined> = {};
  for (const key of Object.keys(match.params)) {
    const value = (match.params as Record<string, string | undefined>)[key];
    decodedParams[key] = value !== undefined ? decodeURIComponent(value) : undefined;
  }
  return { ...match, params: decodedParams as PathMatch<string>['params'] };
}
type TComponent = React.ComponentType<any> & {
  serverPreloadActions?: TServerPreloadActions;
  ssr?: boolean;
  ssrMode?: TRouteSsrMode;
  ssrPreloadActions?: TServerPreloadActions;
};

export type LoadableComponent = {
  load: () => Promise<{
    default: TComponent;
  }>;
  ssr?: boolean;
  ssrMode?: TRouteSsrMode;
  ssrPreloadActions?: TServerPreloadActions;
};

export type TRouteComponent = TComponent | LoadableComponent;

export type TComponentRouteDef = {
  component?: TRouteComponent;
  path: string;
  exact?: boolean;
  strict?: boolean;
  index?: boolean;
  statusCode?: number;
  ssr?: boolean;
  ssrMode?: TRouteSsrMode;
  ssrPreloadActions?: TServerPreloadActions;
  fallback?: React.ComponentType<any>;
  children?: (TComponentRouteDef | TRedirectRouteDef)[];
};

export type TRedirectRouteDef = {
  exact?: boolean;
  from: string;
  statusCode?: number;
  strict?: boolean;
  to: string;
};

export type TRouteDef = TComponentRouteDef | TRedirectRouteDef;

const useRouterInfo = () => {
  const location = useLocation();
  const params = useParams();

  const { getMatches } = useContext(RoutesProvider);
  const matches = getMatches(location.pathname);
  const lastMatch = _.last(matches);

  const match = useMemo(() => {
    return {
      path: lastMatch?.route.path,
      url: lastMatch?.pathname,
      params
    };
  }, [lastMatch, params]);

  return {
    location,
    match
  };
};

export const withRouter = (Component: React.ComponentType<any>) => {
  return (props: any) => {
    const { location, match } = useRouterInfo();
    const navigate = useNavigate();
    return <Component {...props} navigate={navigate} location={location} match={match} />;
  };
};

export const isRedirectRoute = (route: TRouteDef): route is TRedirectRouteDef => {
  return 'from' in route;
};

export const isComponentRoute = (route: TRouteDef): route is TComponentRouteDef => {
  return !('from' in route);
};

/** One pathname per provider, not an unbounded URL cache or a cross-request cache. */
export function createRoutesContext(
  routes: RouteObject[],
  initialMatch?: { pathname: string; matches: ReturnType<typeof matchRoutes> }
) {
  let previousPath = initialMatch?.pathname;
  let matches: ReturnType<typeof matchRoutes> = initialMatch?.matches ?? null;
  return {
    routes,
    getMatches(pathname: string) {
      if (pathname !== previousPath) {
        matches = matchRoutes(routes, pathname);
        previousPath = pathname;
      }
      return matches;
    }
  };
}

export const RoutesProvider = createContext<ReturnType<typeof createRoutesContext>>(
  null as unknown as ReturnType<typeof createRoutesContext>
);

export const useRoutes = () => {
  const context = useContext(RoutesProvider);

  if (!context) {
    throw new Error('useRoutes must be used within a RoutesProvider');
  }

  return context;
};

export const localizeRoutes = (routes: TRouteDef[]): TRouteDef[] => {
  const localePathPrefixes = SUPPORTED_LANGUAGE_PATH_ROOTS.map((code) => (code ? `/${code}` : ''));

  function localizeRoute(route: TRouteDef, prefix: string): TRouteDef {
    if (isRedirectRoute(route)) {
      return {
        ...route,
        from: prefix + route.from,
        to: prefix + route.to
      };
    }

    const children = route.children?.map((child) => localizeRoute(child, prefix));
    return {
      ...route,
      path: prefix + route.path,
      children
    };
  }

  const localized = localePathPrefixes.flatMap((localePrefix) =>
    routes.map((route) => localizeRoute(route, localePrefix))
  );

  localized.push(...routes);

  const redirectLangs = SUPPORTED_LANGUAGE_PATH_ROOTS.filter((root) => root !== '');
  for (const root of redirectLangs) {
    localized.unshift({
      from: `/${root}`,
      to: `/${root}/`,
      strict: true,
      exact: true
    });
  }

  return localized;
};

// generatePath does not URL-encode param values, and route matching decodes %2F
// back into '/', so a raw param could smuggle extra path segments (or a
// scheme-relative '//host') into the redirect target. Encode each param so it
// always stays a single path segment.
const encodeRedirectParams = (params: Record<string, string | undefined>): Record<string, string | undefined> => {
  return _.mapValues(params, (value) => (value === undefined ? value : encodeURIComponent(value)));
};

const generateRedirectPath = (to: string, params: Record<string, string | undefined>): string => {
  return generatePath(to, encodeRedirectParams(params));
};

const ParamRedirect = ({ to }: { to: string }) => {
  const params = useParams();
  return <Navigate to={generateRedirectPath(to, params)} replace={true} />;
};

export const mapRoutesToReactRouterRoutes = (
  routes: TRouteDef[],
  { useLoader = false }: { useLoader?: boolean } = {}
): RouteObject[] => {
  return routes.map<RouteObject>((route) => {
    if (isRedirectRoute(route)) {
      const hasParams = route.to.includes(':');
      return {
        path: route.from,
        loader: ({ params }: any) => {
          const to = hasParams ? generateRedirectPath(route.to, params) : route.to;
          return redirect(to, route.statusCode || 302);
        },
        element: hasParams ? <ParamRedirect to={route.to} /> : <Navigate to={route.to} />
      };
    }

    const loaderData =
      useLoader || route.statusCode !== undefined
        ? {
            loader: () => {
              // Return status-bearing data without throwing so the page still renders.
              return route.statusCode === undefined ? {} : json({}, { status: route.statusCode });
            }
          }
        : {};

    const routeObject = {
      path: route.path,
      element: route.component ? <RouteComponentWrapper component={route.component} /> : null,
      // Client-only: a route render throw (e.g. a third-party ad tag) shows our
      // branded fallback instead of white-screening the SPA. Omitted during SSR so
      // server render errors still fail fast rather than returning a 200 fallback.
      errorElement: canUseDOM ? <RouteErrorBoundary /> : undefined,
      ...loaderData
    };

    if (!route.children) {
      return {
        ...routeObject,
        index: route.index
      };
    }

    return {
      ...routeObject,
      children: mapRoutesToReactRouterRoutes(route.children, { useLoader })
    };
  });
};

export type RouteMatchResult = {
  route: TComponentRouteDef;
  match: PathMatch<string>;
};

type TRouteObjectWithSource = {
  path?: string;
  index?: boolean;
  children?: TRouteObjectWithSource[];
  handle?: {
    sourceRoute: TComponentRouteDef;
  };
};

const mapRoutesToMatchableRoutes = (routes: TRouteDef[]): TRouteObjectWithSource[] => {
  return routes.filter(isComponentRoute).map((route) => ({
    path: route.path,
    index: route.index,
    children: route.children ? mapRoutesToMatchableRoutes(route.children) : undefined,
    handle: {
      sourceRoute: route
    }
  }));
};

export const getRoutesMatches = (routes: TRouteDef[], testPath: string): RouteMatchResult[] => {
  const matches = matchRoutes(mapRoutesToMatchableRoutes(routes) as RouteObject[], testPath);
  if (!matches) {
    return [];
  }

  return matches.map(({ params, pathname, pathnameBase, route }) => {
    const sourceRoute = (route as TRouteObjectWithSource).handle!.sourceRoute;
    return {
      route: sourceRoute,
      match: {
        params,
        pathname,
        pathnameBase,
        pattern: {
          path: sourceRoute.path,
          end: pathname === testPath
        }
      }
    };
  });
};

const RouteComponentWrapper = ({ component: Component }: { component: any }) => {
  const { location, match } = useRouterInfo();
  return <Component location={location} match={match} />;
};
